import { CSRF_HEADER_NAME } from 'configs/vars';
import IHttpService from 'services/http/IHttpService';
import FetchError from 'utils/errors/FetchError';
import UnauthorizedError from 'utils/errors/UnauthorizedError';
import IEntityApiService from './IEntityApiService';
import ILanguageService from 'services/language/ILanguageService';
import ForbiddenError from 'utils/errors/ForbiddenError';
import IAuthTokensService from 'services/auth/IAuthTokensService';

export default class EntityApiServiceFetchImpl implements IEntityApiService {
  constructor(
    private readonly httpService: IHttpService,
    private readonly authTokensService: IAuthTokensService,
    private readonly languageService: ILanguageService
  ) {}

  public readonly getCSRFToken = () => this.authTokensService.getAccessToken();

  public readonly getDefaultHeaders = async () => {
    const csrfToken = await this.getCSRFToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = `${this.authTokensService.getTokenType()} ${csrfToken}`;
    }
    return headers;
  };

  /** Дополняет fetch дефолтными заголовками */
  private readonly fetchWithDefaultHeaders = async (url: string, options?: RequestInit) => {
    const headers = await this.getDefaultHeaders();
    return this.httpService.fetch(url, { ...options, headers: { ...headers, ...options?.headers } });
  };
  // Get
  /** Дополняет fetch дефолтными заголовками без авторизации */
  private readonly fetchWithDefaultHeadersWithoutCredentials = async (url: string, options?: RequestInit) => {
    const headers = { 'Content-Type': 'application/json' };
    return this.httpService.fetch(url, { ...options, headers: { ...headers, ...options?.headers } });
  };

  /** @throws `BackendResponseError` */
  public get = <T>(url: string, options?: RequestInit): Promise<T> => {
    console.debug('[Fetch]: GET', url);
    return this.fetchWithDefaultHeadersWithoutCredentials(url, options).then(this.parseResponse).then(this.parseResponsePayload);
  };

  /** @throws `BackendResponseError` */
  public getWithCredentials = <T>(url: string, options?: RequestInit): Promise<T> => {
    console.debug('[Fetch]: GET', url);
    return this.fetchWithDefaultHeaders(url, {
      method: 'GET',
      credentials: 'include',
      ...options,
    })
      .then(this.parseResponse)
      .then(this.parseResponsePayload);
  };
  /** @throws `BackendResponseError` */
  public getRaw = (url: string, options?: RequestInit): Promise<Response> => {
    console.debug('[Fetch]: GET', url);
    return this.fetchWithDefaultHeaders(url, options);
  };

  /** @throws `BackendResponseError` */
  public getRawWithCredentials = (url: string): Promise<Response> => {
    return this.getRaw(url, {
      method: 'GET',
      credentials: 'include',
    });
  };

  /** @throws `BackendResponseError` */
  public getList = async <T>(url: string, options?: RequestInit): Promise<EntityListData<T>> => {
    console.debug('[Fetch]: GET', url);
    return this.fetchWithDefaultHeadersWithoutCredentials(url, options).then(this.parseResponse).then(this.parseResponsePayloadWithPaging);
  };

  /** @throws `BackendResponseError` */
  public getListWithCredentials = async <T>(url: string, options?: RequestInit): Promise<EntityListData<T>> => {
    console.debug('[Fetch]: GET', url, options);
    return this.fetchWithDefaultHeaders(url, { method: 'GET', credentials: 'include', ...options })
      .then(this.parseResponse)
      .then(this.parseResponsePayloadWithPaging);
  };

  /** @throws `BackendResponseError` */
  public getListWithIdAndNamesOnly = <T>(url: string, options?: RequestInit): Promise<EntityListData<T>> => {
    return this.getListWithCredentials(url, { method: 'GET', credentials: 'include', ...options }); // headers: { 'Content-Type': 'application/vnd.clicker.short+json' } // перестало работать на беке
  };

  // Post

  /** @throws `BackendResponseError` */
  public post = <T>(url: string, body: any = {}, withResponse: boolean = true, options?: RequestInit): Promise<T> => {
    console.debug('[Fetch]: POST', url, body);
    const payload = body;
    return this.httpService
      .fetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
        ...options,
      })
      .then((data: Response) => {
        if (withResponse) return this.parseResponse(data).then(this.parseResponsePayload);
        else return this.parseResponse(data);
      });
  };

  /** @throws `BackendResponseError` */
  public postWithCredentials = async <T>(url: string, body: any = {}, withResponse: boolean = true): Promise<T> => {
    return this.post(url, body, withResponse, { credentials: 'include', headers: await this.getDefaultHeaders() });
  };

  /** @throws `BackendResponseError` */
  public postMultipart = async <T>(url: string, body: FormData): Promise<T> => {
    console.debug('[Fetch]: POST', url, body);
    const csrfToken = await this.getCSRFToken();
    const tokenType = await this.authTokensService.getTokenType();
    return this.httpService
      .fetch(url, {
        method: 'POST',
        body,
        credentials: 'include',
        headers: { [CSRF_HEADER_NAME]: `${tokenType} ${csrfToken}` },
      })
      .then(this.parseResponse)
      .then(this.parseResponsePayload);
  };

  /** @throws `BackendResponseError` */
  public put = (url: string, body: any = {}, options?: RequestInit): Promise<void> => {
    console.debug('[Fetch]: PUT', url, body);
    return this.fetchWithDefaultHeaders(url, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    }).then(this.parseResponse);
  };

  /** @throws `BackendResponseError` */
  public putWithCredentials = (url: string, body: any = {}): Promise<void> => {
    return this.put(url, body, { credentials: 'include' });
  };

  /** @throws `BackendResponseError` */
  public putWithResponse = <T>(url: string, body: any = {}): Promise<T> => {
    console.debug('[Fetch]: PUT', url, body);
    const payload = body;
    return this.fetchWithDefaultHeaders(url, {
      method: 'PUT',
      body: JSON.stringify(payload),
      credentials: 'include',
    }).then((data: Response) => {
      return this.parseResponse(data);
    });
  };

  /** @throws `BackendResponseError` */
  public patch = <T>(url: string, body: any = {}, withResponse?: boolean, options?: RequestInit): Promise<T> => {
    console.debug('[Fetch]: PATCH', url, body);
    return this.fetchWithDefaultHeaders(url, {
      method: 'PATCH',
      body: JSON.stringify(body),
      ...options,
    }).then((data: Response) => {
      if (withResponse) return this.parseResponse(data).then(this.parseResponsePayload);
      else return this.parseResponse(data);
    });
  };

  /** @throws `BackendResponseError` */
  public patchWithCredentials = <T>(url: string, body: any = {}, withResponse?: boolean): Promise<T> => {
    return this.patch(url, body, withResponse, { credentials: 'include' });
  };

  /** @throws `BackendResponseError` */
  public delete = <T = void>(url: string, options?: RequestInit): Promise<T> => {
    console.debug('[Fetch]: DELETE', url);
    return this.fetchWithDefaultHeaders(url, {
      method: 'DELETE',
      ...options,
    }).then(this.parseResponse);
  };

  /** @throws `BackendResponseError` */
  public deleteWithCredentials = (url: string): Promise<void> => {
    return this.delete(url, { credentials: 'include' });
  };

  /** @throws `BackendResponseError` */
  private parseResponse = async (res: Response) => {
    if (res.status === 204) {
      return;
    }
    let details;
    try {
      const contentType = res.headers.get('Content-Type');
      // application/json не используется, т.к. есть кастомные типы, см getListWithIdAndNamesOnly
      if (contentType?.includes('json')) {
        const jsonResponse = await res.json();
        if (res.ok) return jsonResponse;
        else {
          details = jsonResponse.error.details;
          throw new Error(jsonResponse.error?.message || jsonResponse.message);
        } // jsonResponse.error это поле, в котором бэкенд отдаёт ошибки
      } else if (!res.ok) {
        throw new Error();
      }
    } catch (error: Error | any) {
      console.error('[Fetch] Error: ' + error?.message);
      throw this.getError(res.status, error?.message, details, error);
    }
  };

  private parseResponsePayloadWithPaging = ({ data, paging, details }: ResponseContainerDTO): EntityListData<any> => ({
    data: data,
    pagination: paging,
    details: details,
  });
  private parseResponsePayload = (response: ResponseContainerDTO) => response?.data;

  private getError = (
    code: number,
    message?: string,
    details?: Record<string, string>,
    error?: Error
  ): Error | FetchError | UnauthorizedError => {
    // отдельно обрабатываем ошибки, вызванные отменой запросов клиентом
    if (error instanceof Error && error.name === 'AbortError') return error;
    switch (code) {
      case 404:
        if (!message) message = this.languageService.translate(`errors.code.${code}`);
        break;
      case 403:
        if (!message) message = this.languageService.translate(`errors.code.${code}`);
        return new ForbiddenError(message);
      case 401:
        if (!message) message = this.languageService.translate(`errors.code.${code}`);
        return new UnauthorizedError(message);
      default:
        if (!message) message = 'Request failed';
    }
    return new FetchError(message, code, undefined, details);
  };
}
